import { useMemo } from 'react'
import { useParams } from 'react-router'
import dayjs from 'dayjs'

import CopyOutlinedIcon from '@ant-design/icons/CopyOutlined'
import RightOutlinedIcon from '@ant-design/icons/RightOutlined'

import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import { usePatient } from '@shared/providers/src/PatientProvider'
import { OneClickIndicator } from '@shared/utils'

import useInsuranceSummary from '@hooks/useInsuranceSummary'
import { PatientInsurances } from '@pages/Insurances'
import CopyToClipboard from '@components/CopyToClipboard'
import Property from '@components/Property'
import StatusChip from '@components/StatusChip'
import Tile from '@components/Tile'

import InsurancePartner from './InsurancePartner'
import Menu from './Menu'
import { useInsurancesModal } from './Status.hooks'

export default function Status() {
  const { id } = useParams()
  const [insurancesModalOpen, setInsurancesModalOpen] = useInsurancesModal()

  const patient = usePatient()
  const { data: insuranceSummary, isRefreshing, isPending } = useInsuranceSummary(id)

  const statusChips = useMemo(() => {
    if (!patient) return <Skeleton variant="rounded" width={200} height={24} />
    const isWaitlisted = patient.waitlist
    const isSubscribed = patient.status === 'subscribed'
    return (
      <>
        {isWaitlisted && <StatusChip label="Waitlisted" type="warning" />}
        {isSubscribed && <StatusChip label="Subscribed" type="warning" />}
        <StatusChip label={patient.enrolled ? 'Enrolled' : 'Not Enrolled'} type={patient.enrolled ? 'success' : 'warning'} />
        <StatusChip label={patient.active ? 'Active' : 'Inactive'} type={patient.active ? 'success' : 'warning'} />
        <StatusChip label={!patient.disabled ? 'Enabled' : 'Disabled'} type={!patient.disabled ? 'success' : 'secondary'} />
        {patient.lockedOut && <StatusChip label="Locked" type="error" />}
      </>
    )
  }, [patient])

  const insuranceChips = useMemo(() => {
    if (!patient || isPending) return <Skeleton variant="rounded" width={200} height={24} />
    return (
      <>
        <StatusChip icon label={patient.insured ? 'Insured' : 'Not Insured'} type={patient.insured ? 'success' : 'error'} />
        {patient.va && <StatusChip label="VA" type="success" />}
        {insuranceSummary?.medicarePartBIndicator && <StatusChip label="Medicare Part B" type="success" />}
      </>
    )
  }, [patient, isPending, insuranceSummary?.medicarePartBIndicator])

  return (
    <Tile refreshing={isRefreshing} title="Status" action={<Menu />} sx={{ height: '100%' }}>
      <Stack spacing={1} sx={{ px: 2, pt: 0.5, pb: 3 }}>
        <div>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <CopyToClipboard>
                {({ copy }) => <Property label="MRN" value={patient?.id} endAdornment={<CopyIcon />} onClick={() => copy(patient?.id)} />}
              </CopyToClipboard>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Property label="Treatment" value={patient?.treatmentType} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Property label="Enrolled" value={patient?.enrollmentDate ? dayjs(patient?.enrollmentDate).format('L') : ''} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Property
                label="Status"
                value={
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {statusChips}
                  </Stack>
                }
              />
            </Grid>
            {patient?.oneClickIndicator && Object.values(OneClickIndicator).includes(patient.oneClickIndicator) && (
              <Grid size={12}>
                <Property
                  label="One Click"
                  value={
                    <div>
                      {OneClickIndicator.Enrolled === patient.oneClickIndicator && <StatusChip label="Enrolled" type="success" />}
                      {[OneClickIndicator.Declined, OneClickIndicator.DeclinedWithTypo].includes(patient.oneClickIndicator) && (
                        <StatusChip label="Declined" type="error" />
                      )}
                      {OneClickIndicator.NeedToOffer === patient.oneClickIndicator && <StatusChip label="Need to Offer" type="warning" />}
                    </div>
                  }
                />
              </Grid>
            )}
          </Grid>
        </div>

        <Divider />

        {/* Insurance */}
        <Stack
          id="patient-insurance-status"
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
        >
          <Typography variant="h5" sx={{ width: '100%' }}>
            Insurance
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {insuranceSummary?.lastActiveInsurance?.validatedByAvita && <StatusChip label="Validated" type="success" icon />}
            <IconButton size="medium" onClick={() => setInsurancesModalOpen(true)}>
              <RightOutlinedIcon />
            </IconButton>
          </Stack>
        </Stack>

        <PatientInsurances.Modal patientId={id} open={insurancesModalOpen} onClose={() => setInsurancesModalOpen(false)} />

        <InsuranceSection patient={patient} insuranceSummary={insuranceSummary} />

        <Property
          label="Initial"
          value={
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
              {insuranceChips}
            </Stack>
          }
        />

        {patient?.medicaid && (
          <CoverageBanner>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              MEDICAID
            </Typography>
          </CoverageBanner>
        )}

        {patient?.medicare && (
          <CoverageBanner>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              MEDICARE
            </Typography>
          </CoverageBanner>
        )}

        <InsurancePartner insurance={insuranceSummary} patient={patient} isLoading={isPending} />
      </Stack>
    </Tile>
  )
}

function InsuranceSection({ patient, insuranceSummary }) {
  if (!patient || !insuranceSummary) return null

  return (
    <>
      <InsuranceErrorState patient={patient} />
      <InsuranceDetails insurance={insuranceSummary.lastActiveInsurance} />
    </>
  )
}

function InsuranceErrorState({ patient }) {
  const cboRequiresInsurance =
    !patient.insured && patient.cbo?.maintainsUninsuredPatients === false && patient.cbo?.acceptsUninsuredPatients === false
  const dontPrescribe = cboRequiresInsurance && patient.gilead
  const cboInsuranceException =
    !patient.insured &&
    patient.active &&
    patient.cbo?.acceptsUninsuredPatients === false &&
    patient.cbo?.maintainsUninsuredPatients === true

  return (
    <>
      {cboRequiresInsurance && (
        <ErrorBorder>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
            CBO Requires Insurance
          </Typography>
        </ErrorBorder>
      )}
      {cboInsuranceException && (
        <ErrorBorder>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
            CBO Insurance Exception
          </Typography>
        </ErrorBorder>
      )}
      {dontPrescribe && (
        <Stack sx={{ alignItems: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Do not prescribe!
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>exception those with Gilead</Typography>
        </Stack>
      )}
    </>
  )
}

function InsuranceDetails({ insurance }) {
  if (!insurance) {
    return <Typography sx={{ my: 1 }}>No Active Insurance Cards</Typography>
  }

  return (
    <>
      {insurance.assistanceProgram && (
        <InfoBorder>
          <Typography variant="h4">Assistance Program Active</Typography>
        </InfoBorder>
      )}
      <Typography sx={{ fontWeight: 'bold', my: 1 }}>{insurance.company}</Typography>
      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Property label="ID" value={insurance.idnum} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Property label="PCN" value={insurance.pcn} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Property label="BIN" value={insurance.bin} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Property label="RX Grp Num" value={insurance.groupnum} />
        </Grid>
      </Grid>
    </>
  )
}

const CoverageBanner = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.brand.purple,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.common.white,
  padding: theme.spacing(1),
}))

const ErrorBorder = styled(Stack)(({ theme }) => ({
  border: `2px solid ${theme.palette.error.main}`,
  borderRadius: theme.shape.borderRadius,
  alignItems: 'center',
  padding: theme.spacing(1),
}))

const InfoBorder = styled(Stack)(({ theme }) => ({
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  alignItems: 'center',
  color: theme.palette.primary.main,
  padding: theme.spacing(1),
  margin: theme.spacing(1, 0),
}))

const CopyIcon = styled(CopyOutlinedIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
}))
